// Карточка человека: колонка детали с семью вкладками. Потолок — восемь (правило 12).
//
// Вкладка живёт в адресе (?tab=documents): ссылка на документы человека открывается у коллеги
// документами, а не обзором. Состояние вкладок держит Radix, значение ему даёт адрес — одно
// состояние, а не два расходящихся.
//
// Действия над одним человеком стоят в шапке его карточки (правило 3): смена статуса и
// увольнение. Смена статуса — панель, потому что обратима; увольнение — модалка, потому что
// нет. «Рассчитать» не действие, а переход на вкладку «Расчёт сотрудника» раздела «Расчёт»:
// документ по человеку за несколько месяцев, нужнее всего уволенному.

import { RiCalculatorLine, RiPencilLine, RiUser3Line, RiUserSettingsLine } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import { useAuthStore } from '@/shared/auth/store';
import { адресРасчётаСотрудника } from '@/shared/payroll/расчёт-сотрудника';
import { can } from '@/shared/rbac/permissions';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { CardTab as CardTabTrigger, CardTabsList } from '@/shared/ui/card-tabs';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import { STATUS_COLORS, STATUS_TITLES } from '../dictionaries';
import { useEmployeeQuery } from '../queries';
import type { CardTab, PeopleRoute } from '../route';
import { PANEL_EDIT_EMPLOYEE, PANEL_STATUS, cardTab, openPanelHref, peopleHref } from '../route';

import { AccessTab } from './AccessTab';
import { AssignmentsTab } from './AssignmentsTab';
import { DocumentsTab } from './DocumentsTab';
import { DebtsTab } from './DebtsTab';
import { PayoutsTab } from './PayoutsTab';
import { PayslipTab } from './PayslipTab';
import { FireDialog } from './FireDialog';
import { OverviewTab } from './OverviewTab';
import { StatusHistoryTab } from './StatusHistoryTab';

const TAB_TITLES: Readonly<Record<CardTab, string>> = {
  overview: 'Обзор',
  documents: 'Документы',
  payouts: 'Реквизиты',
  debts: 'Долги',
  payslip: 'Расчётный лист',
  assignments: 'Назначения',
  history: 'История статусов',
  access: 'Доступ',
};

const EDIT_PERMISSION = 'core.employee.edit';

/**
 * Право смотреть ведомости, а с ними и расчёт человека: то же, что открывает вкладку «Расчёт
 * сотрудника». Без него кнопки «Рассчитать» в шапке нет: у кадров права на ведомости нет, и
 * ссылка привела бы их в отказ, то есть в тупик, а не к делу.
 */
const PAYROLL_PERMISSION = 'core.payroll.view';

/**
 * Право видеть учётные записи. Без него вкладки «Доступ» нет вовсе — не пустой, а именно нет.
 *
 * Именно НЕ РИСУЕТСЯ, а не прячется стилем: спрятанная вкладка остаётся в разметке, её находит
 * чтение с экрана и ловит палец на планшете. Кадрам, табельщику и рекрутёру список учётных
 * записей не положен вовсе (матрица прав, GET /api/v1/users).
 */
const ACCESS_PERMISSION = 'core.user.view';

export function EmployeeCard({ employeeId, route }: { employeeId: string; route: PeopleRoute }) {
  const navigate = useNavigate();
  const query = useEmployeeQuery(employeeId);
  const tab = cardTab(route.search.tab);
  const permissions = useAuthStore((state) => state.permissions);
  const canEdit = can(permissions, EDIT_PERMISSION);
  const canSeeAccess = can(permissions, ACCESS_PERMISSION);
  const canSettle = can(permissions, PAYROLL_PERMISSION);
  const tabs = (Object.keys(TAB_TITLES) as CardTab[]).filter(
    (key) => key !== 'access' || canSeeAccess,
  );

  return (
    <TabMenu.Root
      value={tab}
      onValueChange={(next) =>
        navigate(peopleHref(route.view, { ...route.search, tab: next }, employeeId))
      }
      className='flex h-full min-w-0 flex-1 flex-col'
    >
      <DetailPane
        title={
          <span className='flex items-center gap-2'>
            <RiUser3Line className='size-5 shrink-0 text-text-soft-400' />
            <span className='truncate'>{query.data?.display_name ?? 'Человек'}</span>
            {query.data !== undefined && (
              // Метка состояния на бумагу не идёт: имя человека в шапке напечатанного
              // документа к месту, а «Работает» над заявлением, которое он подписывает, — нет.
              <Badge.Root
                data-печать='скрыть'
                variant='lighter'
                color={STATUS_COLORS[query.data.status]}
                size='medium'
              >
                {STATUS_TITLES[query.data.status]}
              </Badge.Root>
            )}
          </span>
        }
        actions={
          query.data === undefined ? undefined : (
            <>
              {/* Правка стоит первой: опечатка в фамилии и новый телефон — самая частая
                  работа с карточкой, а до 06.09.2026 её нельзя было сделать вовсе. */}
              {canEdit && (
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='stroke'
                  size='xsmall'
                  onClick={() => navigate(openPanelHref(route, PANEL_EDIT_EMPLOYEE, employeeId))}
                >
                  <Button.Icon as={RiPencilLine} />
                  Править
                </Button.Root>
              )}
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_STATUS, employeeId))}
              >
                <Button.Icon as={RiUserSettingsLine} />
                Сменить статус
              </Button.Root>
              {/* Ссылка, а не действие: на вкладку «Расчёт сотрудника» с этим человеком, где документ
                  строится сразу. Стоит при ЛЮБОМ состоянии, а не только у работающих: уволенному
                  она нужнее всего, с ним надо рассчитаться окончательно. Цель касания 44 точки
                  даёт `цель-тача` внутри кнопки, и ссылка получает её так же, как кнопка. */}
              {canSettle && (
                <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
                  <Link to={адресРасчётаСотрудника(employeeId)}>
                    <Button.Icon as={RiCalculatorLine} />
                    Рассчитать
                  </Link>
                </Button.Root>
              )}
              {query.data.status !== 'fired' && (
                <FireDialog employee={query.data} canEdit={canEdit} />
              )}
            </>
          )
        }
        tabs={
          <CardTabsList>
            {tabs.map((key) => (
              <CardTabTrigger key={key} value={key}>
                {TAB_TITLES[key]}
              </CardTabTrigger>
            ))}
          </CardTabsList>
        }
      >
        <ScreenState
          data={query.data}
          error={query.error}
          subject='карточка человека'
          isEmpty={() => false}
          onRetry={() => void query.refetch()}
          loading={
            <div className='flex flex-col gap-3 p-5'>
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-full' />
            </div>
          }
          empty={null}
        >
          {(employee) => (
            <>
              <TabMenu.Content value='overview'>
                <OverviewTab employee={employee} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='payouts'>
                <PayoutsTab employee={employee} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='debts'>
                <DebtsTab employee={employee} />
              </TabMenu.Content>
              <TabMenu.Content value='payslip'>
                <PayslipTab employee={employee} />
              </TabMenu.Content>
              <TabMenu.Content value='documents'>
                <DocumentsTab employee={employee} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='assignments'>
                <AssignmentsTab employee={employee} route={route} />
              </TabMenu.Content>
              <TabMenu.Content value='history'>
                <StatusHistoryTab employee={employee} />
              </TabMenu.Content>
              {/* Содержимое вкладки — только при праве. Без него вкладку не выбрать, но
                  адрес `?tab=access` человек может получить ссылкой от коллеги, и тогда
                  экран обязан не показать ему чужие доступы. */}
              {canSeeAccess && (
                <TabMenu.Content value='access'>
                  <AccessTab employee={employee} />
                </TabMenu.Content>
              )}
            </>
          )}
        </ScreenState>
      </DetailPane>
    </TabMenu.Root>
  );
}
